// src/components/GenerateApplicationMaterialsButton.jsx

import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FormContext } from "./FormContext";
import { generateApplicationMaterials } from "./apiService";
import { useAuth } from "../util/auth";
import { useRouter } from "../util/router";
import { db } from "../util/firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import Analytics from "../util/analytics";

const useStyles = makeStyles((theme) => ({
  buttonWrapper: {
    display: "inline-block",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  button: {
    minWidth: 150,
    height: 56,
    borderRadius: 28,
    textTransform: "none",
    fontSize: "1.1rem",
    fontWeight: 600,
    boxShadow:
      "0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow:
        "0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08)",
    },
  },
  buttonText: {
    marginLeft: theme.spacing(1),
  },
  loadingIndicator: {
    color: theme.palette.common.white,
    marginRight: theme.spacing(1),
  },
}));

const GenerateApplicationMaterialsButton = ({
  onContentGenerated,
  onGenerateStart,
}) => {
  const classes = useStyles();
  const { formData } = useContext(FormContext);
  const [isLoading, setIsLoading] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const isMounted = useRef(true); // Use ref to track mount status
  const auth = useAuth();
  const router = useRouter();

  // Effect to set isMounted to false on unmount
  useEffect(() => {
    isMounted.current = true; // Set to true on mount
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch user credits from Firestore
  useEffect(() => {
    const fetchUserCredits = async () => {
      if (auth.user) {
        const userDocRef = doc(db, "users", auth.user.uid);
        const userDoc = await getDoc(userDocRef);
        if (isMounted.current) {
          // Check if still mounted after await
          if (userDoc.exists()) {
            const data = userDoc.data();
            const credits = Number(data.credits) || 0;
            setUserCredits(credits);
          } else {
            // Avoid state update if unmounted during setDoc
            await setDoc(userDocRef, { credits: 5, isSubscribed: false });
            if (isMounted.current) {
              setUserCredits(5);
              Analytics.trackFeatureUsage("new_user_credits_initialized");
            }
          }
        }
      }
    };

    fetchUserCredits();
  }, [auth.user]);

  const handleGenerateContent = useCallback(async () => {
    if (!isMounted.current) return;

    // If no credits left, redirect user to pricing
    if (userCredits <= 0) {
      Analytics.trackFeatureUsage("generation_attempt_no_credits");
      router.push("/pricing");
      return;
    }

    const startTime = performance.now();
    setIsLoading(true);
    onGenerateStart();

    Analytics.trackFeatureUsage("generation_started");

    try {
      // If multiple target jobs, orchestrator is called once per job
      const targetJobs = formData.targetJobInformation || [];
      const results = await generateApplicationMaterials(formData, targetJobs);

      // Track number of target jobs
      Analytics.trackFeatureUsage(`target_jobs_count_${targetJobs.length}`);

      // results can be an array (one result per targetJob) or a single object
      const normalizedResults = Array.isArray(results) ? results : [results];

      normalizedResults.forEach((res) => {
        res.keywords = res.atsKeywords || [];
      });

      if (isMounted.current) {
        // Deduct credits based on how many materials are generated
        if (auth.user) {
          const userDocRef = doc(db, "users", auth.user.uid);

          // For each job result, count how many materials are non-empty
          const materialsGenerated = normalizedResults.reduce(
            (total, genContent) => {
              // We'll treat each top-level key as a "material" if not a JSON key or 'keywords'
              const materialTypes = Object.keys(genContent).filter((type) => {
                if (
                  type.endsWith("Json") ||
                  type.endsWith("Errors") ||
                  type === "keywords" ||
                  type === "atsKeywords"
                ) {
                  return false;
                }
                return Boolean(genContent[type]); // must not be empty
              });
              return total + materialTypes.length;
            },
            0
          );

          const newCredits = userCredits - materialsGenerated;

          if (newCredits < 0) {
            Analytics.trackError(
              new Error("Insufficient credits"),
              "GenerateApplicationMaterialsButton"
            );
            alert(
              "You do not have enough credits to generate all the selected materials."
            );
            setIsLoading(false);
            return;
          }

          await updateDoc(userDocRef, { credits: newCredits });
          setUserCredits(newCredits);

          // Track credits usage
          Analytics.trackFeatureUsage(`credits_used_${materialsGenerated}`);
          Analytics.trackFeatureUsage(`credits_remaining_${newCredits}`);
        }

        const endTime = performance.now();
        const generationTime = endTime - startTime;

        // Track successful generation
        Analytics.trackDocumentGeneration("application_materials", true);
        Analytics.trackGenerationTime("application_materials", generationTime);

        // Track individual materials generated
        normalizedResults.forEach((result) => {
          Object.keys(result).forEach((type) => {
            if (
              !type.endsWith("Json") &&
              !type.endsWith("Errors") &&
              type !== "keywords" &&
              type !== "atsKeywords" &&
              result[type]
            ) {
              Analytics.trackDocumentGeneration(type, true);
            }
          });
        });

        // Pass the final normalized results up for handling
        onContentGenerated(normalizedResults);
        // No need to check isMounted here as onContentGenerated should handle its own effects
      }
    } catch (error) {
      console.error("Error calling generateApplicationMaterials:", error);

      // Track error
      Analytics.trackError(error, "GenerateApplicationMaterialsButton");
      Analytics.trackDocumentGeneration("application_materials", false);

      if (isMounted.current) {
        alert(
          "An error occurred while generating application materials. Please try again."
        );
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [
    auth.user,
    formData,
    onContentGenerated,
    onGenerateStart,
    router,
    userCredits,
  ]);

  return (
    <div className={classes.buttonWrapper}>
      {userCredits > 0 ? (
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateContent}
          disabled={isLoading}
          className={classes.button}
          aria-label={
            isLoading
              ? "Generating application materials"
              : "Generate application materials"
          }
          startIcon={
            isLoading && (
              <CircularProgress
                size={20}
                className={classes.loadingIndicator}
              />
            )
          }
        >
          <Typography variant="button" className={classes.buttonText}>
            {isLoading ? "Generating..." : "Generate Application Materials"}
          </Typography>
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            Analytics.trackFeatureUsage("upgrade_plan_click");
            router.push("/pricing");
          }}
          className={classes.button}
          aria-label="Upgrade Plan"
        >
          <Typography variant="button" className={classes.buttonText}>
            Upgrade Plan
          </Typography>
        </Button>
      )}
    </div>
  );
};

GenerateApplicationMaterialsButton.propTypes = {
  onContentGenerated: PropTypes.func.isRequired,
  onGenerateStart: PropTypes.func.isRequired,
};

export default React.memo(GenerateApplicationMaterialsButton);
